import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { useWindowScroll } from "react-use";
import logo from "../../../assets/gema_logo.png";
import { useIsMounted } from "../../../hooks/use-is-mounted";
import { RootState } from "../../../redux/store";
import cn from "../../../utils/classNames";
import Sidebar from "../_sidebar/sidebar";
import HamburgerButton from "../settings/hamburger-button";
import ProfileMenu from "../settings/profile-menu";
import { ActionIcon, Tooltip } from "rizzui";
import { IoArrowBackCircleSharp } from "react-icons/io5";
import { MdArrowBack } from "react-icons/md";
import { useAtom } from "jotai";
import { goBackAtom } from "../../../atom/atom";

type StickyHeaderProps = {
    className?: string;
    offset?: number;
};

function StickyHeader({
    offset = 2,
    className,
    children,
}: React.PropsWithChildren<StickyHeaderProps>) {

    const isMounted = useIsMounted();
    const windowScroll = useWindowScroll();
    return (
        <header
            className={cn(
                "sticky top-0 z-[9999] flex items-center bg-gray-0/80 p-2 backdrop-blur-xl dark:bg-gray-50/50 md:px-5 lg:px-6",
                ((isMounted && windowScroll.y) as number) > offset ? "card-shadow" : "",
                className
            )}
        >
            {children}
        </header>
    );
}

function HeaderMenuRight() {

    return (
        <div className="ms-auto flex shrink-0 items-center gap-2 text-gray-700 xs:gap-3 xl:gap-4">
            <ProfileMenu
                buttonClassName="w-auto sm:w-auto p-1"
                avatarClassName="!w-7 !h-7 sm:!h-8 sm:!w-8"
            />
        </div>
    );
}

export default function Header() {

    // STATES

    const [lastUserId, setLastUserId] = useState("");
    const user = useSelector((state: RootState) => state.auth.user);
    const [goBackState] = useAtom(goBackAtom);
    const navigate = useNavigate();

    // USE EFFECT

    useEffect(() => {
        if (user && user?.id !== lastUserId) {
            setLastUserId(user.id);
        }

        return () => {
            setLastUserId("");
        }
    }, [user])

    // RENDER

    return (
        <StickyHeader
            className={"z-[990] justify-between 2xl:py-5 2xl:pl-6 3xl:px-8"}
        >

            {goBackState.goBack ? (
                <Tooltip content="Volver" placement='top' className="!z-1200">
                <ActionIcon
                    onClick={() => {
                        if (goBackState.goBackRoute) {
                            navigate(goBackState.goBackRoute);
                        } else {
                            navigate(-1);
                        }
                    }}
                    variant="outline"
                    as="span"
                    size="lg"
                    className="border-black h-9 w-9 bg-black"
                >
                    <MdArrowBack className="h-8 w-8 text-white  transition-colors" />
                </ActionIcon>
                </Tooltip>
            ) : (
                <div className="hidden items-center gap-3 xl:flex">
                </div>
            )}

            <div className="flex w-full items-center gap-5 xl:w-auto 3xl:gap-6">
                <div className="flex w-full max-w-2xl items-center xl:w-auto">
                    <HamburgerButton
                        view={<Sidebar className="static w-full 2xl:w-full" />}
                    />
                    <Link
                        aria-label="Site Logo"
                        to={"/"}
                        className="me-4 w-9 shrink-0 text-gray-800 hover:text-gray-900 lg:me-5 xl:hidden"
                    >
                        <img src={logo} width={"80px"} alt="Logo de Gema" />
                    </Link>
                </div>

                <HeaderMenuRight />
            </div>
        </StickyHeader>
    );
}